import { createSelector } from "reselect";
import { createAction } from "redux-actions";
import { concat, path, union, prop, without, sort, filter, propEq, propOr } from "ramda";

export const ADD_ANNOTATION = "USER@ANNOTATION::ADD";
export const ADD_ANNOTATION_MULTIPLE = "USER@ANNOTATION::ADD_MULTIPLE";
export const REMOVE_ANNOTATION = "USER@ANNOTATION::REMOVE";
export const UPDATE_ANNOTATION = "USER@ANNOTATION::UPDATE";

export const SELECT_ANNOTATION = "USER@ANNOTATION::SELECT";
export const DESELECT_ANNOTATION = "USER@ANNOTATION::DESELECT";
export const CLEAR_SELECT_ANNOTATION = "USER@ANNOTATION::SELECT_CLEAR";
export const DELETE_SELECTED = "USER@ANNOTATION::DELETE_SELECTED";
export const DELETE_ALL_ANNOTATIONS = "USER@ANNOTATION::DELETE_ALL_ANNOTATIONS";
export const CLEAR_TO_DELETE = "USER@ANNOTATION::CLEAR_TO_DELETE";

export const addAnnotation = createAction(ADD_ANNOTATION);
export const addAllAnnotations = createAction(ADD_ANNOTATION_MULTIPLE);
export const removeAnnotation = createAction(REMOVE_ANNOTATION);
export const updateAnnotation = createAction(UPDATE_ANNOTATION);

export const addToSelection = createAction(SELECT_ANNOTATION);
export const removeFromSelection = createAction(DESELECT_ANNOTATION);
export const clearSelection = createAction(CLEAR_SELECT_ANNOTATION);
export const deleteSelected = createAction(DELETE_SELECTED, () => null);
export const deleteAllAnnotations = createAction(DELETE_ALL_ANNOTATIONS);
export const clearSelectionToDelete = createAction(CLEAR_TO_DELETE);

const getAnnotations = path(["user", "annotations"]);

export const selectAnnotations = createSelector(getAnnotations, prop("annotations"));

export const selectAnnotationsByPage = page => createSelector(selectAnnotations, filter(propEq("page", page)));

export const selectAnnotationsByPageAndBook = (page, bookId) =>
  createSelector(
    selectAnnotationsByPage(page),
    filter(annotation => {
      const id = propOr("", "bookId", annotation);
      return bookId === id;
    })
  );

export const selectAnnotationsByPageSorted = page =>
  createSelector(
    selectAnnotationsByPage(page),
    sort((a, b) => {
      const aDate = new Date(a.dateCreated);
      const bDate = new Date(b.dateCreated);

      return aDate - bDate;
    })
  );

export const selectToDelete = createSelector(getAnnotations, prop("toDelete"));

const initialState = () => ({
  annotations: [],
  selection: [],
  toDelete: []
});

export default function reducer(state = initialState(), action = {}) {
  switch (action.type) {
    case ADD_ANNOTATION:
      return {
        ...state,
        annotations: [...state.annotations, action.payload]
      };
    case ADD_ANNOTATION_MULTIPLE:
      return {
        ...state,
        annotations: concat(state.annotations, action.payload)
      };
    case REMOVE_ANNOTATION:
      return {
        ...state,
        selection: state.selection.filter(annotation => annotation._id !== action.payload),
        annotations: state.annotations.filter(annotation => annotation._id !== action.payload)
      };
    case UPDATE_ANNOTATION:
      return {
        ...state,
        annotations: state.annotations.map(annotation => {
          if (action.payload._id === annotation._id) {
            return {
              ...annotation,
              _rev: action.payload._rev || annotation._rev,
              shape: action.payload.shape
            };
          }
          return annotation;
        })
      };
    case SELECT_ANNOTATION:
      return {
        ...state,
        selection: union(
          state.selection,
          action.payload.map(annotation => annotation._id || annotation.arrowId)
        )
      };
    case DESELECT_ANNOTATION:
      return {
        ...state,
        selection: without(
          action.payload.map(annotation => annotation._id || annotation.arrowId),
          state.selection
        )
      };
    case CLEAR_SELECT_ANNOTATION:
      return {
        ...state,
        selection: []
      };
    case CLEAR_TO_DELETE:
      return {
        ...state,
        toDelete: []
      };
    case DELETE_SELECTED:
      return {
        ...state,
        annotations: state.annotations.filter(annotation => !state.selection.includes(annotation._id)),
        selection: [],
        toDelete: state.selection
      };
    case DELETE_ALL_ANNOTATIONS: {
      const toDelete = state.annotations.filter(annotation => {
        return action.payload.bookId === annotation.bookId && action.payload.pages.includes(annotation.page);
      });
      return {
        ...state,
        annotations: state.annotations.filter(
          annotation => !(action.payload.bookId === annotation.bookId && action.payload.pages.includes(annotation.page))
        ),
        selection: [],
        toDelete: (state.toDelete ?? []).concat(toDelete.map(annotation => annotation._id))
      };
    }
    default:
      return state;
  }
}
